<template>
  <CompanyAppLayout
    page-title="Dispatch"
    page-subtitle="Crew and equipment callouts across well programs"
  >
    <div class="space-y-6 pt-4">
      <div class="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 class="text-2xl font-bold text-white">Dispatch Board</h1>
          <p class="text-slate-400 text-sm mt-1">Assign hands, trucks, and equipment — then send callouts.</p>
        </div>
        <div class="flex gap-2 flex-wrap">
          <button class="btn-secondary" @click="shiftWeek(-7)">← Prev</button>
          <button class="btn-secondary" @click="goToday">Today</button>
          <button class="btn-secondary" @click="shiftWeek(7)">Next →</button>
          <button class="btn-primary" @click="openCreate()">+ Assignment</button>
        </div>
      </div>

      <p v-if="error" class="text-red-400">{{ error }}</p>
      <p v-if="message" class="text-emerald-400">{{ message }}</p>

      <div class="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div class="stat-card"><p class="text-xs text-slate-400">This Week</p><p class="text-2xl font-bold text-white mt-1">{{ stats.total || 0 }}</p></div>
        <div class="stat-card"><p class="text-xs text-slate-400">Draft</p><p class="text-2xl font-bold text-white mt-1">{{ stats.draft || 0 }}</p></div>
        <div class="stat-card"><p class="text-xs text-amber-400">Notified</p><p class="text-2xl font-bold text-white mt-1">{{ stats.notified || 0 }}</p></div>
        <div class="stat-card"><p class="text-xs text-emerald-400">Confirmed</p><p class="text-2xl font-bold text-white mt-1">{{ stats.confirmed || 0 }}</p></div>
        <div class="stat-card"><p class="text-xs text-rose-400">Urgent</p><p class="text-2xl font-bold text-white mt-1">{{ stats.urgent || 0 }}</p></div>
      </div>

      <div v-if="loading" class="text-slate-400 py-10 text-center">Loading board…</div>

      <div v-else class="board-scroll">
        <div class="board-grid">
          <div v-for="day in board" :key="day.date" class="day-col">
            <div class="day-head">
              <p class="font-semibold text-white">{{ day.label }}</p>
              <p class="text-xs text-slate-500">{{ day.assignments.length }} jobs</p>
            </div>
            <div class="space-y-3">
              <button
                v-for="a in day.assignments"
                :key="a.id"
                class="assign-card text-left w-full"
                :class="['pri-' + a.priority, 'st-' + a.status]"
                @click="openDetail(a)"
              >
                <div class="flex items-start justify-between gap-2">
                  <p class="font-medium text-white text-sm leading-snug">{{ a.title }}</p>
                  <span class="text-[10px] uppercase tracking-wide text-slate-400">{{ a.status.replace('_',' ') }}</span>
                </div>
                <p class="text-xs text-slate-400 mt-1">{{ a.startTime || '—' }}–{{ a.endTime || '—' }} · {{ a.siteName || 'No site' }}</p>
                <p class="text-xs text-emerald-300/90 mt-1">{{ a.employeeName || 'Unassigned' }}</p>
                <p v-if="a.fleetUnit || a.equipmentName" class="text-xs text-slate-500 mt-1">
                  {{ [a.fleetUnit, a.equipmentName].filter(Boolean).join(' · ') }}
                </p>
              </button>
              <p v-if="!day.assignments.length" class="text-xs text-slate-600 text-center py-6">No callouts</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create / edit modal -->
    <div v-if="showForm" class="modal-backdrop" @click.self="showForm = false">
      <div class="modal-panel max-w-2xl">
        <h3 class="text-xl font-semibold text-white mb-4">{{ form.id ? 'Edit Assignment' : 'New Dispatch Assignment' }}</h3>
        <form class="space-y-4" @submit.prevent="saveAssignment">
          <div>
            <label class="form-label">Title *</label>
            <input v-model="form.title" required class="form-input" placeholder="Pad 7 vacuum / swab crew" />
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="form-label">Program</label>
              <input v-model="form.programName" class="form-input" placeholder="Bakken Program A" />
            </div>
            <div>
              <label class="form-label">Customer / Operator</label>
              <input v-model="form.customerName" class="form-input" />
            </div>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label class="form-label">Date *</label>
              <input v-model="form.date" type="date" required class="form-input" />
            </div>
            <div>
              <label class="form-label">Start</label>
              <input v-model="form.startTime" type="time" class="form-input" />
            </div>
            <div>
              <label class="form-label">End</label>
              <input v-model="form.endTime" type="time" class="form-input" />
            </div>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="form-label">Site</label>
              <select v-model="form.siteId" class="form-select">
                <option :value="0">Select site…</option>
                <option v-for="s in sites" :key="s.id" :value="s.id">{{ s.name }}</option>
              </select>
            </div>
            <div>
              <label class="form-label">Employee / Hand</label>
              <select v-model="form.employeeId" class="form-select">
                <option :value="0">Unassigned</option>
                <option v-for="e in employees" :key="e.id" :value="e.id">{{ e.name }} ({{ e.crewLevel || 'hand' }})</option>
              </select>
            </div>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label class="form-label">Truck / Unit</label>
              <select v-model="form.fleetId" class="form-select">
                <option :value="0">None</option>
                <option v-for="f in fleet" :key="f.id" :value="f.id">{{ f.unitNumber }}</option>
              </select>
            </div>
            <div>
              <label class="form-label">Equipment</label>
              <select v-model="form.equipmentId" class="form-select">
                <option :value="0">None</option>
                <option v-for="eq in equipment" :key="eq.id" :value="eq.id">{{ eq.name }}</option>
              </select>
            </div>
            <div>
              <label class="form-label">Priority</label>
              <select v-model="form.priority" class="form-select">
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div>
            <label class="form-label">Notes</label>
            <textarea v-model="form.notes" rows="2" class="form-input"></textarea>
          </div>
          <label v-if="!form.id" class="flex items-center gap-2 text-sm text-slate-300">
            <input v-model="form.notify" type="checkbox" class="rounded" />
            Send callout immediately after create
          </label>
          <p v-if="formError" class="text-red-400 text-sm">{{ formError }}</p>
          <div class="flex justify-end gap-3">
            <button type="button" class="btn-secondary" @click="showForm = false">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="saving">{{ saving ? 'Saving…' : 'Save' }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Detail drawer -->
    <div v-if="selected" class="modal-backdrop" @click.self="selected = null">
      <div class="modal-panel max-w-lg">
        <div class="flex items-start justify-between gap-3">
          <div>
            <p class="text-xs text-slate-500 uppercase">{{ selected.priority }} · {{ selected.status.replace('_',' ') }}</p>
            <h3 class="text-xl font-semibold text-white mt-1">{{ selected.title }}</h3>
            <p class="text-slate-400 text-sm mt-1">{{ selected.date }} · {{ selected.startTime }}–{{ selected.endTime }}</p>
          </div>
          <button class="text-slate-400 hover:text-white" @click="selected = null">✕</button>
        </div>
        <dl class="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div><dt class="text-slate-500">Site</dt><dd class="text-white">{{ selected.siteName || '—' }}</dd></div>
          <div><dt class="text-slate-500">Hand</dt><dd class="text-white">{{ selected.employeeName || 'Unassigned' }}</dd></div>
          <div><dt class="text-slate-500">Unit</dt><dd class="text-white">{{ selected.fleetUnit || '—' }}</dd></div>
          <div><dt class="text-slate-500">Equipment</dt><dd class="text-white">{{ selected.equipmentName || '—' }}</dd></div>
          <div class="col-span-2"><dt class="text-slate-500">Customer</dt><dd class="text-white">{{ selected.customerName || '—' }}</dd></div>
          <div v-if="selected.notes" class="col-span-2"><dt class="text-slate-500">Notes</dt><dd class="text-slate-300">{{ selected.notes }}</dd></div>
          <div v-if="selected.calloutMessage" class="col-span-2"><dt class="text-slate-500">Last callout</dt><dd class="text-slate-300">{{ selected.calloutMessage }}</dd></div>
        </dl>
        <div class="flex flex-wrap gap-2 mt-6">
          <button class="btn-primary" :disabled="busy" @click="notify(selected.id)">Send Callout</button>
          <button class="btn-secondary" :disabled="busy" @click="setStatus(selected.id, 'confirm')">Confirm</button>
          <button class="btn-secondary" :disabled="busy" @click="setStatus(selected.id, 'start')">Start</button>
          <button class="btn-secondary" :disabled="busy" @click="setStatus(selected.id, 'complete')">Complete</button>
          <button class="btn-danger" :disabled="busy" @click="setStatus(selected.id, 'cancel')">Cancel</button>
          <button class="btn-secondary" @click="editSelected">Edit</button>
          <button
            v-if="['draft', 'cancelled'].includes(selected.status)"
            class="btn-danger"
            :disabled="busy"
            @click="deleteAssignment(selected)"
          >Delete</button>
        </div>
      </div>
    </div>
  </CompanyAppLayout>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'
import { buildApiUrl } from '@/config/api'

const loading = ref(true)
const saving = ref(false)
const busy = ref(false)
const error = ref('')
const message = ref('')
const formError = ref('')
const showForm = ref(false)
const selected = ref<any>(null)
const board = ref<any[]>([])
const stats = reactive<any>({})
const employees = ref<any[]>([])
const sites = ref<any[]>([])
const fleet = ref<any[]>([])
const equipment = ref<any[]>([])
const weekStart = ref(mondayOf(new Date()))

const form = reactive<any>({
  id: 0,
  title: '',
  programName: '',
  customerName: '',
  date: weekStart.value,
  startTime: '07:00',
  endTime: '19:00',
  siteId: 0,
  employeeId: 0,
  fleetId: 0,
  equipmentId: 0,
  priority: 'normal',
  notes: '',
  notify: true,
})

function mondayOf(d: Date) {
  const x = new Date(d)
  const day = (x.getDay() + 6) % 7
  x.setDate(x.getDate() - day)
  return x.toISOString().slice(0, 10)
}

function weekEnd(start: string) {
  const x = new Date(start + 'T12:00:00')
  x.setDate(x.getDate() + 6)
  return x.toISOString().slice(0, 10)
}

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`, 'Content-Type': 'application/json' }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const from = weekStart.value
    const to = weekEnd(from)
    const res = await fetch(buildApiUrl(`company/dispatch?from=${from}&to=${to}`), { headers: authHeaders() })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to load dispatch')
    board.value = data.board || []
    Object.assign(stats, data.stats || {})
    employees.value = data.employees || []
    sites.value = data.sites || []
    fleet.value = data.fleet || []
    equipment.value = data.equipment || []
  } catch (e: any) {
    error.value = e.message || 'Failed to load'
  } finally {
    loading.value = false
  }
}

function shiftWeek(days: number) {
  const x = new Date(weekStart.value + 'T12:00:00')
  x.setDate(x.getDate() + days)
  weekStart.value = mondayOf(x)
  load()
}

function goToday() {
  weekStart.value = mondayOf(new Date())
  load()
}

function openCreate(date?: string) {
  form.id = 0
  form.title = ''
  form.programName = ''
  form.customerName = ''
  form.date = date || weekStart.value
  form.startTime = '07:00'
  form.endTime = '19:00'
  form.siteId = 0
  form.employeeId = 0
  form.fleetId = 0
  form.equipmentId = 0
  form.priority = 'normal'
  form.notes = ''
  form.notify = true
  formError.value = ''
  showForm.value = true
}

function openDetail(a: any) {
  selected.value = a
}

function editSelected() {
  if (!selected.value) return
  const a = selected.value
  form.id = a.id
  form.title = a.title
  form.programName = a.programName || ''
  form.customerName = a.customerName || ''
  form.date = a.date
  form.startTime = a.startTime || '07:00'
  form.endTime = a.endTime || '19:00'
  form.siteId = a.siteId || 0
  form.employeeId = a.employeeId || 0
  form.fleetId = a.fleetId || 0
  form.equipmentId = a.equipmentId || 0
  form.priority = a.priority || 'normal'
  form.notes = a.notes || ''
  form.notify = false
  selected.value = null
  showForm.value = true
}

async function saveAssignment() {
  saving.value = true
  formError.value = ''
  try {
    const payload = {
      title: form.title,
      programName: form.programName || null,
      customerName: form.customerName || null,
      date: form.date,
      startTime: form.startTime,
      endTime: form.endTime,
      siteId: form.siteId || null,
      employeeId: form.employeeId || null,
      fleetId: form.fleetId || null,
      equipmentId: form.equipmentId || null,
      priority: form.priority,
      notes: form.notes || null,
      notify: !!form.notify && !form.id,
    }
    const url = form.id
      ? buildApiUrl(`company/dispatch/${form.id}/update`)
      : buildApiUrl('company/dispatch')
    const res = await fetch(url, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Save failed')
    showForm.value = false
    message.value = form.id ? 'Assignment updated' : 'Assignment created'
    await load()
  } catch (e: any) {
    formError.value = e.message || 'Save failed'
  } finally {
    saving.value = false
  }
}

async function notify(id: number) {
  busy.value = true
  error.value = ''
  try {
    const res = await fetch(buildApiUrl(`company/dispatch/${id}/notify`), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ channel: 'sms' }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Notify failed')
    message.value = data.recipient
      ? `Callout queued to ${data.recipient}`
      : 'Callout recorded (no phone/email on hand)'
    selected.value = data.assignment
    await load()
  } catch (e: any) {
    error.value = e.message || 'Notify failed'
  } finally {
    busy.value = false
  }
}

async function deleteAssignment(assignment: any) {
  if (!window.confirm(`Delete dispatch assignment “${assignment.title}”? This cannot be undone.`)) return
  busy.value = true
  error.value = ''
  try {
    const res = await fetch(buildApiUrl(`company/dispatch/${assignment.id}`), {
      method: 'DELETE',
      headers: authHeaders(),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || 'Delete failed')
    selected.value = null
    message.value = 'Assignment deleted'
    await load()
  } catch (e: any) {
    error.value = e.message || 'Delete failed'
  } finally {
    busy.value = false
  }
}

async function setStatus(id: number, action: string) {
  busy.value = true
  try {
    const res = await fetch(buildApiUrl(`company/dispatch/${id}/${action}`), {
      method: 'POST',
      headers: authHeaders(),
      body: '{}',
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Update failed')
    selected.value = data.assignment
    message.value = `Marked ${action}`
    await load()
  } catch (e: any) {
    error.value = e.message || 'Update failed'
  } finally {
    busy.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.stat-card { @apply bg-slate-800/50 border border-slate-700/50 rounded-2xl p-4; }
.board-scroll { @apply overflow-x-auto pb-2; }
.board-grid { @apply grid gap-3; grid-template-columns: repeat(7, minmax(180px, 1fr)); min-width: 1260px; }
.day-col { @apply bg-slate-800/40 border border-slate-700/50 rounded-2xl p-3 min-h-[420px]; }
.day-head { @apply mb-3 pb-2 border-b border-slate-700/60; }
.assign-card { @apply bg-slate-900/70 border border-slate-700/70 rounded-xl p-3 hover:border-emerald-500/40 transition-colors; }
.assign-card.pri-urgent { @apply border-rose-500/50; }
.assign-card.pri-high { @apply border-amber-500/40; }
.assign-card.st-completed { @apply opacity-60; }
.form-label { @apply block text-sm text-slate-400 mb-1; }
.form-input, .form-select { @apply w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white; }
.btn-primary { @apply inline-flex items-center px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-medium disabled:opacity-50; }
.btn-secondary { @apply inline-flex items-center px-4 py-2 rounded-xl bg-slate-700/60 border border-slate-600 text-slate-200 disabled:opacity-50; }
.btn-danger { @apply inline-flex items-center px-4 py-2 rounded-xl bg-red-500/20 border border-red-500/40 text-red-300 disabled:opacity-50; }
.modal-backdrop { @apply fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4; }
.modal-panel { @apply w-full bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl; }
</style>
